import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import { useDevice } from 'src/contexts/device-context';

// ----------------------------------------------------------------------

function createSSRMatchMedia(deviceType) {
  // Simulate viewport widths based on device type
  const simulatedWidth = {
    desktop: 1280,
    tablet: 960,
    mobile: 600
  }[deviceType] || 1280; // default to desktop

  return (query) => {
    // Parse media query to extract min-width or max-width values
    const minWidthMatch = query.match(/min-width:\s*(\d+)px/);
    const maxWidthMatch = query.match(/max-width:\s*(\d+(?:\.\d+)?)px/);

    let matches = false;

    if (minWidthMatch) {
      const minWidth = parseInt(minWidthMatch[1], 10);
      matches = simulatedWidth >= minWidth;
    } else if (maxWidthMatch) {
      const maxWidth = parseFloat(maxWidthMatch[1]);
      matches = simulatedWidth <= maxWidth;
    }

    return { matches };
  };
}

export function useResponsive(query, start, end, options = {}) {
  const theme = useTheme();
  const { deviceType } = useDevice();

  // Create default options with SSR matching
  const defaultOptions = {
    ssrMatchMedia: createSSRMatchMedia(deviceType),
    ...options
  };

  const mediaUp = useMediaQuery(theme.breakpoints.up(start), defaultOptions);

  const mediaDown = useMediaQuery(theme.breakpoints.down(start), defaultOptions);

  const mediaBetween = useMediaQuery(theme.breakpoints.between(start, end), defaultOptions);

  const mediaOnly = useMediaQuery(theme.breakpoints.only(start), defaultOptions);

  if (query === 'up') {
    return mediaUp;
  }

  if (query === 'down') {
    return mediaDown;
  }

  if (query === 'between') {
    return mediaBetween;
  }

  return mediaOnly;
}

// ----------------------------------------------------------------------

export function useWidth() {
  const theme = useTheme();

  const keys = [...theme.breakpoints.keys].reverse();

  return (
    keys.reduce((output, key) => {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const matches = useMediaQuery(theme.breakpoints.up(key));

      return !output && matches ? key : output;
    }, null) || 'xs'
  );
}
